import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useBusinessLine, BUSINESS_LINES } from '../contexts/BusinessLineContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { formatDate, leadStatusColors, leadSourceColors, businessLineColors } from '../lib/utils';
import {
  Plus,
  Search,
  MoreVertical,
  Edit,
  Trash2,
  UserPlus,
  Filter,
  Facebook,
  Instagram,
  Linkedin,
  Youtube,
  MessageCircle,
  Upload,
  Download,
  CalendarDays,
  Cake,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { ColumnCustomizer } from '../components/ColumnCustomizer';
import { ImportExportManager } from '../components/ImportExportManager';
import { SalesReport } from '../components/SalesReport';
import { PolicyConversionModal } from '../components/PolicyConversionModal';
import { BusinessSelectionModal } from '../components/BusinessSelectionModal';
import { FileSpreadsheet } from 'lucide-react';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';

const toYMD = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Definición de columnas para personalización
const defaultLeadColumns = [
  { id: 'name', label: 'Lead', visible: true, order: 0 },
  { id: 'contact', label: 'Contacto', visible: true, order: 1 },
  { id: 'contact_date', label: 'Fecha de Contacto', visible: true, order: 2 },
  { id: 'next_follow_up_date', label: 'Próximo Contacto', visible: true, order: 2.5 },
  { id: 'birth_date', label: 'Fecha de Nacimiento', visible: true, order: 3 },
  { id: 'source', label: 'Fuente', visible: true, order: 4 },
  { id: 'lead_type', label: 'Tipo', visible: true, order: 4.5 },
  { id: 'business_lines', label: 'Aseguradora', visible: true, order: 5 },
  { id: 'business_types', label: 'Negocio/Plan', visible: true, order: 6 },
  { id: 'policies_count', label: 'Pólizas', visible: true, order: 7 },
  { id: 'aportaciones', label: 'Aportaciones', visible: true, order: 8 },
  { id: 'assigned_agent', label: 'Agente', visible: true, order: 9 },
  { id: 'status', label: 'Estado', visible: true, order: 10 },
  { id: 'actions', label: 'Acciones', visible: true, order: 11 }
];

// Custom icon components
const TikTokIcon = () => <span className="font-bold text-xs">TT</span>;
const WebsiteIcon = () => <span className="font-bold text-xs">W</span>;
const ReferidoIcon = () => <span className="font-bold text-xs">R</span>;
const ManualIcon = () => <span className="font-bold text-xs">M</span>;

const sourceIcons = {
  facebook: Facebook,
  instagram: Instagram,
  linkedin: Linkedin,
  youtube: Youtube,
  whatsapp: MessageCircle,
  tiktok: TikTokIcon,
  website: WebsiteIcon,
  referido: ReferidoIcon,
  manual: ManualIcon,
  'Llamada en frío': ManualIcon
};

// Moved outside component to avoid defining during render
const SourceIcon = ({ source }) => {
  const Icon = sourceIcons[source] || sourceIcons['Llamada en frío'];
  return (
    <div className={`p-1.5 rounded-full ${leadSourceColors[source] || 'bg-gray-500'} text-white`}>
      <Icon className="h-3 w-3" />
    </div>
  );
};

export const LeadsPage = () => {
  const { user, canCreate, canEdit, canDelete, isAdmin, isMaster, isAsistente, isDeveloper } = useAuth();
  const showAgentesTab = isAsistente || isAdmin || isMaster;
  const [searchParams, setSearchParams] = useSearchParams();
  const leadTab = searchParams.get('leadTab') || 'prospectos';
  const { selectedLine, isAllLines, filterByBusinessLine, availableLines } = useBusinessLine();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortField, setSortField] = useState('contact_date');
  const [sortDir, setSortDir] = useState('desc');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [columns, setColumns] = useState(defaultLeadColumns);
  const [policyModalOpen, setPolicyModalOpen] = useState(false);
  const [convertingLead, setConvertingLead] = useState(null);
  const [bizSelectLeadOpen, setBizSelectLeadOpen]  = useState(false);
  const [bizSelectLead, setBizSelectLead]           = useState(null);
  const [users, setUsers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [managedUsers, setManagedUsers] = useState([]);
  const [agentFilter, setAgentFilter] = useState(user?.id || '');
  const [customFields, setCustomFields] = useState([]);
  const [customFieldValues, setCustomFieldValues] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [contactDateOpen, setContactDateOpen] = useState(false);
  const [nextFollowUpOpen, setNextFollowUpOpen] = useState(false);
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchDebounced, setSearchDebounced] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    birth_date: '',
    contact_date: new Date().toISOString().split('T')[0],
    next_follow_up: '',
    status: 'nuevo',
    source: 'manual',
    source_campaign: '',
    notes: '',
    business_lines: [],
    business_types: [],
    aportaciones: '',
    periodicidad: 'mensual',
    plazo: '',
    assigned_agent_id: user?.id || '',
    lead_type: 'cliente'
  });

  const setLeadTab = (tab) => {
    setSearchParams(prev => { const p = new URLSearchParams(prev); p.set('leadTab', tab); return p; });
    setCurrentPage(1);
  };

  // Debounce search 400ms
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    fetchLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, sourceFilter, currentPage, searchDebounced, agentFilter, leadTab, selectedLine]);

  useEffect(() => {
    axios.get(`${API}/users`).then(r => {
      setUsers(r.data);
      if (isAsistente) {
        const managed = user?.managed_agents || [];
        if (managed.length > 0) {
          setManagedUsers(r.data.filter(u => managed.includes(u.id)));
        }
      }
    }).catch(() => {});
    fetchCustomFields();
    if (isAdmin || isDeveloper) {
      axios.get(`${API}/agents`).then(r => setAgents(r.data)).catch(() => {});
    }
  }, []);

  const fetchCustomFields = () => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/custom-fields`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setCustomFields(r.data))
      .catch(() => {});
  };

  const handleCreateCustomField = async (fieldData) => {
    const token = localStorage.getItem('token');
    const res = await axios.post(`${API}/custom-fields`, fieldData, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setCustomFields(prev => [...prev, res.data]);
    return res.data;
  };

  const fetchLeads = async () => {
    try {
      const params = new URLSearchParams({ page: currentPage, page_size: 20 });
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (sourceFilter !== 'all') params.append('source', sourceFilter);
      if (searchDebounced) params.append('search', searchDebounced);
      if ((isAdmin || isAsistente || isDeveloper) && agentFilter && agentFilter !== 'all') params.append('agent_id', agentFilter);
      if (showAgentesTab && leadTab === 'agentes') params.append('lead_type', 'agente');
      else if (showAgentesTab && leadTab === 'prospectos') params.append('lead_type', 'cliente');
      if (!isAllLines) params.append('business_line', selectedLine);
      const response = await axios.get(`${API}/leads?${params.toString()}`);
      const data = response.data;
      setLeads(data.items || []);
      setTotalPages(data.total_pages || 1);
      setTotalRecords(data.total || 0);
    } catch (error) {
      toast.error('Error al cargar leads');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = {};
    if (!formData.contact_date?.trim()) errors.contact_date = 'La fecha de contacto es obligatoria';
    if (!editingLead && !formData.next_follow_up?.trim()) errors.next_follow_up = 'La próxima fecha de contacto es obligatoria';
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});
    try {
      const payload = {
        ...formData,
        email: formData.email?.trim() || null,
        phone: formData.phone?.trim() || null,
        source_campaign: formData.source_campaign?.trim() || null,
        notes: formData.notes?.trim() || null,
        birth_date: formData.birth_date?.trim() || null,
        contact_date: formData.contact_date?.trim() || null,
        next_follow_up_date: formData.next_follow_up?.trim() || null,
        status: formData.status || 'nuevo',
        contributions: parseFloat(formData.aportaciones) || 0,
        plazo: parseInt(formData.plazo) || 0
      };
      
      if (editingLead) {
        await axios.put(`${API}/leads/${editingLead.id}`, payload);
        if (Object.keys(customFieldValues).length > 0) {
          const token = localStorage.getItem('token');
          await axios.patch(
            `${API}/leads/${editingLead.id}/custom-fields`,
            customFieldValues,
            { headers: { Authorization: `Bearer ${token}` } }
          );
        }
        toast.success('Lead actualizado');
      } else {
        await axios.post(`${API}/leads`, payload);
        toast.success('Lead creado');
      }
      // Cerrar modal primero
      setDialogOpen(false);
      resetForm();
      // Luego refrescar datos
      await fetchLeads();
    } catch (error) {
      console.error('Error al guardar lead:', error);
      const errorMsg = Array.isArray(error.response?.data?.detail)
        ? error.response.data.detail.map(e => e.msg).join(', ')
        : (error.response?.data?.detail || 'Error al guardar lead');
      toast.error(errorMsg);
    }
  };

  const handleEdit = (lead) => {
    setEditingLead(lead);
    setFormErrors({});
    setContactDateOpen(false);
    setNextFollowUpOpen(false);
    setFormData({
      name: lead.name,
      email: lead.email || '',
      phone: lead.phone || '',
      birth_date: lead.birth_date || '',
      contact_date: lead.contact_date || '',
      next_follow_up: lead.next_follow_up_date || '',
      status: lead.status || 'nuevo',
      source: lead.source,
      source_campaign: lead.source_campaign || '',
      notes: lead.notes || '',
      business_lines: lead.business_lines || [],
      business_types: lead.business_types || [],
      aportaciones: (lead.contributions ?? lead.aportaciones ?? '').toString(),
      periodicidad: lead.periodicidad || 'mensual',
      plazo: (lead.plazo && lead.plazo !== 0) ? lead.plazo.toString() : '',
      assigned_agent_id: lead.assigned_agent_id || '',
      lead_type: lead.lead_type || 'cliente'
    });
    setCustomFieldValues(lead.custom_values || {});
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este lead?')) return;
    try {
      await axios.delete(`${API}/leads/${id}`);
      toast.success('Lead eliminado');
      fetchLeads();
    } catch (error) {
      toast.error('Error al eliminar lead');
    }
  };

  const handleStatusChange = async (leadId, newStatus) => {
    if (newStatus === 'ganado') {
      const lead = leads.find(l => l.id === leadId);
      if (lead) {
        const businessList = Array.isArray(lead.business_types) ? lead.business_types : [];
        if (businessList.length > 1) {
          // Show business selection modal before converting
          setBizSelectLead(lead);
          setBizSelectLeadOpen(true);
          return;
        }
        // Single or no business: go straight to PolicyConversionModal
        setConvertingLead(lead);
        setPolicyModalOpen(true);
      }
      return;
    }
    // Normal status change
    try {
      await axios.put(`${API}/leads/${leadId}`, { status: newStatus });
      setLeads(prev => prev.map(l => l.id === leadId ? { ...l, status: newStatus } : l));
    } catch {
      toast.error('Error al actualizar estado');
    }
  };

  const resetForm = () => {
    setEditingLead(null);
    setCustomFieldValues({});
    setFormErrors({});
    setContactDateOpen(false);
    setNextFollowUpOpen(false);
    setFormData({
      name: '',
      email: '',
      phone: '',
      birth_date: '',
      contact_date: new Date().toISOString().split('T')[0],
      next_follow_up: '',
      status: 'nuevo',
      source: 'Llamada en frío',
      source_campaign: '',
      notes: '',
      business_lines: selectedLine !== 'all' ? [selectedLine] : [],
      business_types: [],
      aportaciones: '',
      periodicidad: 'mensual',
      plazo: '',
      assigned_agent_id: user?.id || '',
      lead_type: (showAgentesTab && leadTab === 'agentes') ? 'agente' : 'cliente'
    });
  };

  // Filter by business line context and then by search
  // All filtering is server-side; leads is the current page
  const filteredLeads = leads;

  // Toggle business line selection
  const toggleBusinessLine = (lineId) => {
    setFormData(prev => ({
      ...prev,
      business_lines: prev.business_lines.includes(lineId)
        ? prev.business_lines.filter(id => id !== lineId)
        : [...prev.business_lines, lineId]
    }));
  };

  // Manejar tipos de negocio/plan
  const [newBusinessType, setNewBusinessType] = useState('');
  const [showSalesReport, setShowSalesReport] = useState(false);
  
  const addBusinessType = () => {
    const trimmed = newBusinessType.trim();
    if (!trimmed) return;
    setFormData(prev => {
      const types = prev.business_types || [];
      if (types.includes(trimmed)) return prev;
      return { ...prev, business_types: [...types, trimmed] };
    });
    setNewBusinessType('');
  };

  const removeBusinessType = (type) => {
    setFormData(prev => ({
      ...prev,
      business_types: (prev.business_types || []).filter(t => t !== type)
    }));
  };

  const handleSort = (field) => {
    setSortDir(prev => sortField === field ? (prev === 'asc' ? 'desc' : 'asc') : 'asc');
    setSortField(field);
  };
  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-40 inline-block flex-shrink-0" />;
    return sortDir === 'asc'
      ? <ArrowUp className="h-3 w-3 ml-1 inline-block flex-shrink-0" />
      : <ArrowDown className="h-3 w-3 ml-1 inline-block flex-shrink-0" />;
  };

  const sortedLeads = [...leads].sort((a, b) => {
    const va = (a[sortField] ?? '').toString();
    const vb = (b[sortField] ?? '').toString();
    return sortDir === 'asc' ? va.localeCompare(vb, undefined, { numeric: true, sensitivity: 'base' }) : vb.localeCompare(va, undefined, { numeric: true, sensitivity: 'base' });
  });

  const visibleLeadCols = [...columns]
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .filter(c => c.visible)
    .filter(c => {
      if (c.id === 'lead_type') return showAgentesTab && leadTab === 'prospectos';
      if (showAgentesTab && leadTab === 'agentes') {
        return !['business_types', 'policies_count'].includes(c.id);
      }
      return true;
    });

  return (
    <div className="space-y-6" data-testid="leads-page">
      {showSalesReport ? (
        <SalesReport onBack={() => setShowSalesReport(false)} />
      ) : (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Leads</h1>
              <p className="text-muted-foreground">Gestiona tus prospectos y oportunidades</p>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => setShowSalesReport(true)}
                data-testid="sales-report-btn"
              >
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Ver Reporte de Ventas
              </Button>
              {canCreate && (
                <Dialog open={dialogOpen} onOpenChange={(open) => {
                  setDialogOpen(open);
                  if (!open) resetForm();
                }}>
                  <DialogTrigger asChild>
                    <Button className="bg-[#2d8659] hover:bg-[#2d8659]/90" onClick={resetForm} data-testid="new-lead-btn">
                      <Plus className="h-4 w-4 mr-2" />
                      Nuevo Lead
                    </Button>
                  </DialogTrigger>
            <DialogContent className="sm:max-w-[500px]">
              <DialogHeader>
                <DialogTitle>{editingLead ? 'Editar Lead' : 'Nuevo Lead'}</DialogTitle>
                <DialogDescription>
                  {editingLead ? 'Modifica la información del lead' : 'Ingresa los datos del nuevo prospecto'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="name">Nombre *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Nombre completo"
                      required
                      data-testid="lead-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="correo@ejemplo.com"
                      data-testid="lead-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Teléfono</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+52 55 1234 5678"
                      data-testid="lead-phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Fecha de Contacto *</Label>
                    <Popover open={contactDateOpen} onOpenChange={setContactDateOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="lead-contact-date-input">
                          <CalendarDays className="mr-2 h-4 w-4 text-muted-foreground" />
                          {formData.contact_date || <span className="text-muted-foreground">Seleccionar fecha</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={formData.contact_date ? new Date(formData.contact_date + 'T00:00:00') : undefined}
                          onSelect={(date) => {
                            if (date) setFormData({ ...formData, contact_date: toYMD(date) });
                            setContactDateOpen(false);
                          }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    {formErrors.contact_date && <p className="text-xs text-red-500">{formErrors.contact_date}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="birth_date">Fecha Nacimiento</Label>
                    <Input
                      id="birth_date"
                      type="date"
                      value={formData.birth_date}
                      onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
                      data-testid="lead-birth-date-input"
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Próxima fecha de contacto *</Label>
                    <Popover open={nextFollowUpOpen} onOpenChange={setNextFollowUpOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full justify-start text-left font-normal">
                          <CalendarDays className="mr-2 h-4 w-4 text-muted-foreground" />
                          {formData.next_follow_up || <span className="text-muted-foreground">Seleccionar fecha</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={formData.next_follow_up ? new Date(formData.next_follow_up + 'T00:00:00') : undefined}
                          onSelect={(date) => {
                            if (date) setFormData({ ...formData, next_follow_up: toYMD(date) });
                            setNextFollowUpOpen(false);
                          }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    {formErrors.next_follow_up && <p className="text-xs text-red-500">{formErrors.next_follow_up}</p>}
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Estado</Label>
                    <Select
                      value={formData.status}
                      onValueChange={(value) => setFormData({ ...formData, status: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Nuevo (default)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="nuevo">Nuevo</SelectItem>
                        <SelectItem value="contactado">Contactado</SelectItem>
                        <SelectItem value="en_negociacion">En Negociación</SelectItem>
                        <SelectItem value="pendiente_de_pago">Pendiente de pago</SelectItem>
                        <SelectItem value="ganado">Ganado</SelectItem>
                        <SelectItem value="perdido">Perdido</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="source">Fuente</Label>
                    <Select
                      value={formData.source}
                      onValueChange={(value) => setFormData({ ...formData, source: value })}
                    >
                      <SelectTrigger data-testid="lead-source-select">
                        <SelectValue placeholder="Selecciona fuente" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Llamada en frío">Llamada en frío</SelectItem>
                        <SelectItem value="facebook">Facebook</SelectItem>
                        <SelectItem value="instagram">Instagram</SelectItem>
                        <SelectItem value="whatsapp">WhatsApp</SelectItem>
                        <SelectItem value="linkedin">LinkedIn</SelectItem>
                        <SelectItem value="youtube">YouTube</SelectItem>
                        <SelectItem value="tiktok">TikTok</SelectItem>
                        <SelectItem value="website">Sitio Web</SelectItem>
                        <SelectItem value="referido">Referido</SelectItem>
                        <SelectItem value="Mercado cálido">Mercado cálido</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="source_campaign">Campaña (opcional)</Label>
                    <Input
                      id="source_campaign"
                      value={formData.source_campaign}
                      onChange={(e) => setFormData({ ...formData, source_campaign: e.target.value })}
                      placeholder="Nombre de campaña"
                    />
                  </div>
                  {(isAdmin || isAsistente || isDeveloper) && formData.lead_type !== 'agente' && (
                  <div className="col-span-2 space-y-2">
                    <Label>Agente</Label>
                    <select
                      value={formData.assigned_agent_id || ''}
                      onChange={(e) => setFormData({ ...formData, assigned_agent_id: e.target.value })}
                      className="w-full border rounded px-3 py-2 text-sm bg-background"
                    >
                      <option value="">— Sin asignar —</option>
                      {[...users].sort((a, b) => (a.full_name || '').localeCompare(b.full_name || '', 'es')).map(u => (
                        <option key={u.id} value={u.id}>{u.full_name} ({u.role})</option>
                      ))}
                    </select>
                  </div>
                  )}
                  {(isAdmin || isAsistente) && (
                  <div className="col-span-2 space-y-2">
                    <Label>Tipo de lead</Label>
                    <Select
                      value={formData.lead_type}
                      onValueChange={(value) => setFormData({ ...formData, lead_type: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecciona tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cliente">Cliente</SelectItem>
                        <SelectItem value="agente">Agente</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  )}
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="notes">Notas</Label>
                    <Textarea
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Notas adicionales sobre el lead..."
                      rows={3}
                      data-testid="lead-notes-input"
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Negocio/Tipo de Plan</Label>
                    <div className="flex gap-2">
                      <Input
                        value={newBusinessType}
                        onChange={(e) => setNewBusinessType(e.target.value)}
                        placeholder="Ej: OptiMaxx Plus, Vida, Auto..."
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addBusinessType())}
                      />
                      <Button type="button" variant="outline" onClick={addBusinessType}>
                        Agregar
                      </Button>
                    </div>
                    {formData.business_types.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {formData.business_types.map((type) => (
                          <Badge
                            key={type}
                            variant="secondary"
                            className="cursor-pointer hover:bg-red-100"
                            onClick={() => removeBusinessType(type)}
                          >
                            {type} ×
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="aportaciones">Aportaciones/Prima (MXN)</Label>
                    <Input
                      id="aportaciones"
                      type="number"
                      value={formData.aportaciones}
                      onChange={(e) => setFormData({ ...formData, aportaciones: e.target.value })}
                      placeholder="5000"
                      min="0"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="periodicidad">Periodicidad</Label>
                    <Select
                      value={formData.periodicidad}
                      onValueChange={(value) => setFormData({ ...formData, periodicidad: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecciona periodicidad" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mensual">Mensual</SelectItem>
                        <SelectItem value="trimestral">Trimestral</SelectItem>
                        <SelectItem value="semestral">Semestral</SelectItem>
                        <SelectItem value="anual">Anual</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label htmlFor="plazo">Plazo (años)</Label>
                    <Input
                      id="plazo"
                      type="number"
                      value={formData.plazo}
                      onChange={(e) => setFormData({ ...formData, plazo: e.target.value })}
                      placeholder="15"
                      min="1"
                      max="99"
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Aseguradora *</Label>
                    <div className="flex flex-wrap gap-3 p-3 border rounded-lg">
                      {availableLines.filter(l => l.id !== 'all').map(line => (
                        <div key={line.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`line-${line.id}`}
                            checked={formData.business_lines.includes(line.id)}
                            onCheckedChange={() => toggleBusinessLine(line.id)}
                          />
                          <label
                            htmlFor={`line-${line.id}`}
                            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2"
                          >
                            <div className={`w-2 h-2 rounded-full ${line.color}`}></div>
                            {line.name}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                {editingLead && customFields.length > 0 && (
                  <div className="col-span-2 space-y-3 pt-2 border-t">
                    <p className="text-sm font-medium text-muted-foreground">Campos personalizados</p>
                    <div className="grid grid-cols-2 gap-3">
                      {customFields.map(cf => (
                        <div key={cf.id} className={`space-y-1 ${cf.field_type === 'lista' ? 'col-span-2' : ''}`}>
                          <Label>{cf.label}</Label>
                          {cf.field_type === 'texto' && (
                            <Input
                              value={customFieldValues[cf.id] || ''}
                              onChange={e => setCustomFieldValues(prev => ({ ...prev, [cf.id]: e.target.value }))}
                              placeholder={cf.label}
                            />
                          )}
                          {cf.field_type === 'numero' && (
                            <Input
                              type="number"
                              value={customFieldValues[cf.id] || ''}
                              onChange={e => setCustomFieldValues(prev => ({ ...prev, [cf.id]: e.target.value }))}
                              placeholder="0"
                            />
                          )}
                          {cf.field_type === 'fecha' && (
                            <Input
                              type="date"
                              value={customFieldValues[cf.id] || ''}
                              onChange={e => setCustomFieldValues(prev => ({ ...prev, [cf.id]: e.target.value }))}
                            />
                          )}
                          {cf.field_type === 'booleano' && (
                            <Select
                              value={customFieldValues[cf.id] || ''}
                              onValueChange={v => setCustomFieldValues(prev => ({ ...prev, [cf.id]: v }))}
                            >
                              <SelectTrigger><SelectValue placeholder="Selecciona" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="true">Sí</SelectItem>
                                <SelectItem value="false">No</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                          {cf.field_type === 'lista' && (
                            <Select
                              value={customFieldValues[cf.id] || ''}
                              onValueChange={v => setCustomFieldValues(prev => ({ ...prev, [cf.id]: v }))}
                            >
                              <SelectTrigger><SelectValue placeholder="Selecciona una opción" /></SelectTrigger>
                              <SelectContent>
                                {(cf.options || []).map(opt => (
                                  <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex justify-end gap-3">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button
                    type="submit"
                    className="bg-[#1a365d] hover:bg-[#1a365d]/90"
                    data-testid="lead-submit-btn"
                    disabled={formData.business_lines.length === 0}
                  >
                    {editingLead ? 'Actualizar' : 'Crear Lead'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
              )}
              <ImportExportManager
                module="leads"
                apiUrl={API}
                data={leads}
                onSuccess={fetchLeads}
              />
            </div>
          </div>

      {/* Tabs Prospectos / Agentes — solo para asistente, admin y master */}
      {showAgentesTab && (
        <div className="border-b">
          <nav className="-mb-px flex space-x-8">
            {[
              { id: 'prospectos', label: 'Prospectos' },
              { id: 'agentes',    label: 'Agentes'    },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setLeadTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                  leadTab === tab.id
                    ? 'border-[#1a365d] text-[#1a365d] dark:border-blue-400 dark:text-blue-400'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nombre, email o teléfono..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                className="pl-10"
                data-testid="lead-search"
              />
            </div>
            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setCurrentPage(1); }}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="status-filter">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                <SelectItem value="nuevo">Nuevo</SelectItem>
                <SelectItem value="contactado">Contactado</SelectItem>
                <SelectItem value="en_negociacion">En Negociación</SelectItem>
                <SelectItem value="pendiente_de_pago">Pendiente de pago</SelectItem>
                <SelectItem value="ganado">Ganado</SelectItem>
                <SelectItem value="perdido">Perdido</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sourceFilter} onValueChange={(v) => { setSourceFilter(v); setCurrentPage(1); }}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="source-filter">
                <SelectValue placeholder="Fuente" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las fuentes</SelectItem>
                <SelectItem value="facebook">Facebook</SelectItem>
                <SelectItem value="instagram">Instagram</SelectItem>
                <SelectItem value="whatsapp">WhatsApp</SelectItem>
                <SelectItem value="linkedin">LinkedIn</SelectItem>
                <SelectItem value="youtube">YouTube</SelectItem>
                <SelectItem value="tiktok">TikTok</SelectItem>
                <SelectItem value="website">Sitio Web</SelectItem>
                <SelectItem value="referido">Referido</SelectItem>
                <SelectItem value="manual">Llamada en frío</SelectItem>
              </SelectContent>
            </Select>
            {(isAdmin || isMaster || isDeveloper || isAsistente) && (
              <Select value={agentFilter} onValueChange={(v) => { setAgentFilter(v); setCurrentPage(1); }}>
                <SelectTrigger className="w-full sm:w-[200px]" data-testid="lead-agent-filter">
                  <SelectValue placeholder="Agente" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={user?.id || ''}>Mis Leads</SelectItem>
                  {!isAsistente && <SelectItem value="all">Todos</SelectItem>}
                  {[...(isAsistente ? managedUsers : agents)]
                    .filter(a => {
                      const uid = isAsistente ? a.id : a.user_id;
                      return uid !== user?.id;
                    })
                    .sort((a, b) => {
                      const na = isAsistente ? (a.full_name || '') : (a.user?.full_name || '');
                      const nb = isAsistente ? (b.full_name || '') : (b.user?.full_name || '');
                      return na.localeCompare(nb, 'es');
                    })
                    .map(a => {
                      const uid = isAsistente ? a.id : a.user_id;
                      const name = isAsistente ? a.full_name : (a.user?.full_name || a.user_id);
                      return (
                        <SelectItem key={uid} value={uid}>
                          {name}
                        </SelectItem>
                      );
                    })
                  }
                </SelectContent>
              </Select>
            )}
            <ColumnCustomizer
              columns={defaultLeadColumns}
              storageKey="leads"
              onColumnsChange={setColumns}
              extraColumns={customFields}
              onCreateCustomField={handleCreateCustomField}
              isAdmin={isAdmin}
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table className="min-w-max" wrapperClassName="overflow-auto max-h-[calc(100vh-16rem)]">
            <TableHeader className="sticky top-0 z-20 bg-white dark:bg-gray-950 border-b shadow-sm">
              <TableRow>
                {visibleLeadCols.map(col => {
                  if (col.id === 'name') return (
                    <TableHead key="name" className="min-w-[180px] cursor-pointer select-none hover:bg-muted/30 whitespace-nowrap" onClick={() => handleSort('name')}>
                      <div className="flex items-center gap-1">Lead <SortIcon field="name" /></div>
                    </TableHead>
                  );
                  if (col.id === 'contact') return <TableHead key="contact" className="min-w-[160px]">Contacto</TableHead>;
                  if (col.id === 'contact_date') return (
                    <TableHead key="contact_date" className="min-w-[155px] cursor-pointer select-none hover:bg-muted/30 whitespace-nowrap" onClick={() => handleSort('contact_date')}>
                      <div className="flex items-center gap-1">Fecha de Contacto <SortIcon field="contact_date" /></div>
                    </TableHead>
                  );
                  if (col.id === 'next_follow_up_date') return (
                    <TableHead key="next_follow_up_date" className="min-w-[155px] cursor-pointer select-none hover:bg-muted/30 whitespace-nowrap" onClick={() => handleSort('next_follow_up_date')}>
                      <div className="flex items-center gap-1">Próximo Contacto <SortIcon field="next_follow_up_date" /></div>
                    </TableHead>
                  );
                  if (col.id === 'birth_date') return (
                    <TableHead key="birth_date" className="min-w-[150px] cursor-pointer select-none hover:bg-muted/30 whitespace-nowrap" onClick={() => handleSort('birth_date')}>
                      <div className="flex items-center gap-1">Fecha Nacimiento <SortIcon field="birth_date" /></div>
                    </TableHead>
                  );
                  if (col.id === 'source') return (
                    <TableHead key="source" className="min-w-[100px] cursor-pointer select-none hover:bg-muted/30 whitespace-nowrap" onClick={() => handleSort('source')}>
                      <div className="flex items-center gap-1">Fuente <SortIcon field="source" /></div>
                    </TableHead>
                  );
                  if (col.id === 'lead_type') return <TableHead key="lead_type" className="min-w-[80px]">Tipo</TableHead>;
                  if (col.id === 'business_lines') return <TableHead key="business_lines" className="min-w-[130px]">Aseguradora</TableHead>;
                  if (col.id === 'business_types') return <TableHead key="business_types" className="min-w-[140px]">Negocio/Plan</TableHead>;
                  if (col.id === 'policies_count') return <TableHead key="policies_count" className="min-w-[80px]">Pólizas</TableHead>;
                  if (col.id === 'aportaciones') return <TableHead key="aportaciones" className="min-w-[130px]">Aportaciones</TableHead>;
                  if (col.id === 'assigned_agent') return (
                    <TableHead key="assigned_agent" className="min-w-[140px] cursor-pointer select-none hover:bg-muted/30 whitespace-nowrap" onClick={() => handleSort('assigned_agent_name')}>
                      <div className="flex items-center gap-1">Agente <SortIcon field="assigned_agent_name" /></div>
                    </TableHead>
                  );
                  if (col.id === 'status') return (
                    <TableHead key="status" className="min-w-[170px] cursor-pointer select-none hover:bg-muted/30 whitespace-nowrap" onClick={() => handleSort('status')}>
                      <div className="flex items-center gap-1">Estado <SortIcon field="status" /></div>
                    </TableHead>
                  );
                  if (col.id === 'actions') return <TableHead key="actions" className="min-w-[80px] text-right">Acciones</TableHead>;
                  if (col.id.startsWith('cf_')) return (
                    <TableHead key={col.id} className="min-w-[140px] whitespace-nowrap">{col.label}</TableHead>
                  );
                  return null;
                })}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={12} className="text-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#1a365d] mx-auto"></div>
                  </TableCell>
                </TableRow>
              ) : leads.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} className="text-center py-8 text-muted-foreground">
                    No se encontraron leads
                  </TableCell>
                </TableRow>
              ) : (
                sortedLeads.map((lead) => (
                  <TableRow key={lead.id} className="data-row" data-testid={`lead-row-${lead.id}`}>
                    {visibleLeadCols.map(col => {
                      if (col.id === 'name') return (
                        <TableCell key="name">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="font-medium">{lead.name}</span>
                            {lead.source === 'reporte_ventas' && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300">
                                Reporte
                              </span>
                            )}
                            {lead.lead_type === 'agente' && (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300">
                                Agente
                              </span>
                            )}
                          </div>
                          {lead.source_campaign && (
                            <div className="text-xs text-muted-foreground">{lead.source_campaign}</div>
                          )}
                        </TableCell>
                      );
                      if (col.id === 'contact') return (
                        <TableCell key="contact">
                          <div className="text-sm">{lead.email || '-'}</div>
                          <div className="text-xs text-muted-foreground">{lead.phone || '-'}</div>
                        </TableCell>
                      );
                      if (col.id === 'contact_date') return (
                        <TableCell key="contact_date" className="text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <CalendarDays className="h-3 w-3" />
                            {formatDate(lead.contact_date || lead.created_at)}
                          </div>
                        </TableCell>
                      );
                      if (col.id === 'next_follow_up_date') return (
                        <TableCell key="next_follow_up_date" className="text-sm text-muted-foreground">
                          {lead.next_follow_up_date ? (
                            <div className="flex items-center gap-1">
                              <CalendarDays className="h-3 w-3" />
                              {formatDate(lead.next_follow_up_date)}
                            </div>
                          ) : '—'}
                        </TableCell>
                      );
                      if (col.id === 'birth_date') return (
                        <TableCell key="birth_date">
                          {lead.birth_date ? (
                            <div className="flex items-center gap-1 text-sm">
                              <Cake className="h-3 w-3 text-pink-500" />
                              {formatDate(lead.birth_date)}
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">-</span>
                          )}
                        </TableCell>
                      );
                      if (col.id === 'source') return (
                        <TableCell key="source">
                          <div className="flex items-center gap-2">
                            <SourceIcon source={lead.source} />
                            <span className="text-sm capitalize">{lead.source}</span>
                          </div>
                        </TableCell>
                      );
                      if (col.id === 'lead_type') return (
                        <TableCell key="lead_type">
                          {lead.lead_type === 'agente'
                            ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300">Agente</span>
                            : <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">Cliente</span>
                          }
                        </TableCell>
                      );
                      if (col.id === 'business_lines') return (
                        <TableCell key="business_lines">
                          <div className="flex flex-wrap gap-1">
                            {(lead.business_lines || []).map(lineId => {
                              const line = BUSINESS_LINES.find(l => l.id === lineId);
                              return line ? (
                                <Badge key={lineId} className={`${businessLineColors[lineId]} text-xs`}>
                                  {line.name}
                                </Badge>
                              ) : null;
                            })}
                            {(!lead.business_lines || lead.business_lines.length === 0) && (
                              <span className="text-xs text-muted-foreground">-</span>
                            )}
                          </div>
                        </TableCell>
                      );
                      if (col.id === 'business_types') return (
                        <TableCell key="business_types">
                          <div className="flex flex-wrap gap-1">
                            {(lead.business_types || []).slice(0, 2).map((type, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs">
                                {type}
                              </Badge>
                            ))}
                            {(lead.business_types || []).length > 2 && (
                              <Popover>
                                <PopoverTrigger asChild>
                                  <Badge variant="secondary" className="text-xs cursor-pointer hover:bg-secondary/80">
                                    +{lead.business_types.length - 2}
                                  </Badge>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-2" align="start">
                                  <div className="flex flex-col gap-1">
                                    <p className="text-xs font-semibold text-muted-foreground mb-1">Planes ocultos:</p>
                                    {lead.business_types.slice(2).map((type, idx) => (
                                      <Badge key={idx} variant="outline" className="text-xs justify-start">
                                        {type}
                                      </Badge>
                                    ))}
                                  </div>
                                </PopoverContent>
                              </Popover>
                            )}
                            {(!lead.business_types || lead.business_types.length === 0) && (
                              <span className="text-xs text-muted-foreground">-</span>
                            )}
                          </div>
                        </TableCell>
                      );
                      if (col.id === 'policies_count') return (
                        <TableCell key="policies_count">
                          <Badge variant="secondary">{lead.policies_count ?? (lead.business_types?.length || 0)}</Badge>
                        </TableCell>
                      );
                      if (col.id === 'aportaciones') return (
                        <TableCell key="aportaciones">
                          {(lead.contributions || lead.aportaciones) ? (
                            <div className="text-sm">
                              <span className="font-medium">${(lead.contributions || lead.aportaciones)?.toLocaleString()}</span>
                              {lead.periodicidad && (
                                <span className="text-xs text-muted-foreground ml-1">
                                  /{lead.periodicidad.slice(0, 3)}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">-</span>
                          )}
                        </TableCell>
                      );
                      if (col.id === 'assigned_agent') return (
                        <TableCell key="assigned_agent">
                          <span className="text-sm text-muted-foreground">
                            {lead.assigned_agent_name || (lead.assigned_agent_id ? lead.assigned_agent_id.slice(0, 8) + '…' : '—')}
                          </span>
                        </TableCell>
                      );
                      if (col.id === 'status') return (
                        <TableCell key="status">
                          {canEdit ? (
                            <Select
                              value={lead.status}
                              onValueChange={(value) => handleStatusChange(lead.id, value)}
                            >
                              <SelectTrigger className="w-[160px] h-8">
                                <Badge className={`${leadStatusColors[lead.status]} border-0`}>
                                  {lead.status === 'pendiente_de_pago' ? 'Pendiente de pago' : lead.status}
                                </Badge>
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="nuevo">Nuevo</SelectItem>
                                <SelectItem value="contactado">Contactado</SelectItem>
                                <SelectItem value="en_negociacion">En Negociación</SelectItem>
                                <SelectItem value="pendiente_de_pago">Pendiente de pago</SelectItem>
                                <SelectItem value="ganado">Ganado</SelectItem>
                                <SelectItem value="perdido">Perdido</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <Badge className={`${leadStatusColors[lead.status] || 'bg-gray-100 text-gray-800'} border-0`}>
                              {lead.status === 'pendiente_de_pago' ? 'Pendiente de pago' : lead.status}
                            </Badge>
                          )}
                        </TableCell>
                      );
                      if (col.id === 'actions') return (
                        <TableCell key="actions" className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid={`lead-actions-${lead.id}`}>
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {canEdit && (
                                <DropdownMenuItem onClick={() => handleEdit(lead)}>
                                  <Edit className="h-4 w-4 mr-2" />
                                  Editar
                                </DropdownMenuItem>
                              )}
                              {canCreate && (
                                <DropdownMenuItem onClick={() => { setConvertingLead(lead); setPolicyModalOpen(true); }}>
                                  <UserPlus className="h-4 w-4 mr-2" />
                                  Convertir a cliente
                                </DropdownMenuItem>
                              )}
                              {(canDelete || isAsistente) && (
                                <DropdownMenuItem
                                  onClick={() => handleDelete(lead.id)}
                                  className="text-red-600"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Eliminar
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      );
                      if (col.id.startsWith('cf_')) {
                        const fieldId = col.id.slice(3);
                        const cf = customFields.find(f => f.id === fieldId);
                        const val = (lead.custom_values || {})[fieldId];
                        let display = val != null && val !== '' ? val : '-';
                        if (cf?.field_type === 'booleano' && val != null) {
                          display = val === 'true' ? 'Sí' : 'No';
                        }
                        return (
                          <TableCell key={col.id} className="text-muted-foreground">
                            {display}
                          </TableCell>
                        );
                      }
                      return null;
                    })}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Paginación */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 py-2">
          <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="gap-1">
            <ChevronLeft className="h-4 w-4" />Anterior
          </Button>
          <span className="text-sm text-muted-foreground whitespace-nowrap">
            Página {currentPage} de {totalPages} &nbsp;·&nbsp; Total: {totalRecords} leads
          </span>
          <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} className="gap-1">
            Siguiente<ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
        </>
      )}

      <BusinessSelectionModal
        open={bizSelectLeadOpen}
        onOpenChange={setBizSelectLeadOpen}
        businessTypes={bizSelectLead?.business_types || []}
        onConfirm={async (selected, remaining) => {
          if (!bizSelectLead) return;
          if (remaining.length === 0) {
            // Full conversion: open PolicyConversionModal
            setConvertingLead(bizSelectLead);
            setPolicyModalOpen(true);
          } else {
            // Partial conversion: call API directly
            try {
              await axios.put(`${API}/leads/${bizSelectLead.id}`, {
                status: 'ganado',
                converted_business_types: selected,
                remaining_business_types: remaining,
              });
              toast.success(`Conversión parcial: ${selected.join(', ')} convertidos`);
              fetchLeads();
            } catch {
              toast.error('Error al convertir lead');
            }
          }
          setBizSelectLead(null);
        }}
      />
      <PolicyConversionModal
        open={policyModalOpen}
        onOpenChange={(v) => { setPolicyModalOpen(v); if (!v) setConvertingLead(null); }}
        lead={convertingLead}
        apiUrl={API}
        isAdmin={isAdmin}
        onSuccess={({ removeFromList, lead: updatedLead }) => {
          if (removeFromList) {
            // Full conversion (paid) OR pending payment → client created, remove from leads list
            setLeads(prev => prev.filter(l => l.id !== convertingLead?.id));
          } else {
            // Partial conversion paid → lead stays with remaining businesses
            setLeads(prev => prev.map(l =>
              l.id === updatedLead?.id ? { ...l, status: updatedLead.status } : l
            ));
          }
        }}
      />
    </div>
  );
};
